'use client';

import { useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { MdClose } from 'react-icons/md';
import { Button } from '@/components/ui/button/Button';
import { ButtonType } from '@/components/ui/button/Button.types';
import { Text } from '@/components/ui/text/Text';
import { Colors } from '@/lib/colorUtils';
import { ReservationTimeDetails } from '@/components/myaccount/reservation-time-details';
import { AddToAppleWallet } from '@/components/ui/add-to-apple-wallet';
import { AddToGoogleWallet } from '@/components/ui/add-to-google-wallet';
import { resolveLogoSrc } from '@/lib/theme-utils';
import { formatUsPhone } from '@/lib/contact-utils';
import { getBase64QRCode } from '@/lib/qr-code';
import { formatReservationTimeInGarageTz } from '@/lib/date-time-utils';
import { buildReservationApplePassUrl } from '@/lib/wallet-pass';
import { useThemeStore } from '@/stores/theme.store';
import type {
  ReservationPassModalProps,
  ReservationPassVariant,
} from './ReservationPassModal.types';
import { useFocusTrap } from '@/hooks/useFocusTrap';

const HEADER_TITLE_KEY: Record<ReservationPassVariant, string> = {
  reservation: 'reservation',
  ticket: 'ticket',
  guestPass: 'guestPass',
};

export const ReservationPassModal = ({
  open,
  reservation,
  onClose,
  variant,
  guestPassTitle,
  qrValue: qrValueProp,
  garageTimezone,
}: ReservationPassModalProps) => {
  const t = useTranslations('Reservations');
  const tCommon = useTranslations('Common');
  const dialogRef = useRef<HTMLDivElement>(null);
  const theme = useThemeStore((s) => s.theme);
  const companyThemes = useThemeStore((s) => s.companyThemes);
  const companyTheme = (reservation.company && companyThemes[reservation.company]) || theme;

  const resolvedVariant: ReservationPassVariant =
    variant ?? (reservation.reservationType === 'GUEST_PASS' ? 'guestPass' : 'reservation');

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, handleEscape]);

  const isGuestPass = reservation.reservationType === 'GUEST_PASS';
  const isTicket = resolvedVariant === 'ticket';

  const startDate = reservation.checkinAfterTime
    ? new Date(reservation.checkinAfterTime)
    : new Date();
  const endDate = reservation.checkoutBeforeTime
    ? new Date(reservation.checkoutBeforeTime)
    : undefined;

  const vehicleText = reservation.vehicleInfo
    ? [
        reservation.vehicleInfo.vehicleLicensePlate,
        [
          reservation.vehicleInfo.vehicleColor,
          reservation.vehicleInfo.vehicleMake,
          reservation.vehicleInfo.vehicleModel,
        ]
          .filter(Boolean)
          .join(' '),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const garageFullAddress =
    [
      reservation.garageAddress,
      [reservation.garageCity, reservation.garageState].filter(Boolean).join(', '),
      reservation.garageZip,
    ]
      .filter(Boolean)
      .join('\n') ||
    // Parking-history tickets only carry the garage name, not a street address.
    reservation.garageName ||
    '';
  const garagePhone = formatUsPhone(reservation.garagePhone);

  // Show the human reservation/guest-pass code only — never fall back to
  // reservationId (a document-id UUID is not a meaningful code to display). PB-1590.
  const displayCode = reservation.vendorReservationId || '';
  // Ticket header uses the ticket number only — no reservationId/UUID fallback, which overflows and distorts the modal (PB-1482).
  const ticketNumber = reservation.vendorReservationId || '';
  // Tickets pass an explicit claim-tag URL; reservations/guest passes fall back to the code.
  const qrValue = qrValueProp ?? reservation.vendorReservationId ?? '';
  // The claim-tag URL is ~90 chars; at the 110px Figma size its modules drop to ~2px, which the POS
  // scanner cannot read off a phone screen (PB-1483). Short reservation codes keep the spec size.
  const isClaimTagQr = !!qrValueProp;
  const price = reservation.value != null ? `$${reservation.value.toFixed(2)}` : '';
  // Guest Pass pages pass the tenant's configured title; every other caller keeps the static key.
  const headerTitle =
    resolvedVariant === 'guestPass' && guestPassTitle?.trim()
      ? guestPassTitle
      : t(HEADER_TITLE_KEY[resolvedVariant] as Parameters<typeof t>[0]);

  const applePassUrl = useMemo(
    () =>
      buildReservationApplePassUrl(
        reservation.company ?? '',
        reservation.reservationId ?? ''
      ),
    [reservation.company, reservation.reservationId]
  );

  const qrDataUrl = useMemo(() => {
    if (!qrValue) return '';
    try {
      return isClaimTagQr
        ? getBase64QRCode(qrValue, 'L', 184, 12)
        : getBase64QRCode(qrValue, 'M', 110, 20);
    } catch {
      return '';
    }
  }, [qrValue, isClaimTagQr]);

  if (!open) return null;

  const content = (
    <>
      <div
        className="tw:fixed tw:inset-0 tw:z-40 tw:bg-black/40 tw:transition-opacity animate-backdrop-fade-in"
        aria-hidden="true"
        onClick={onClose}
        data-cy="reservation-pass-backdrop"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reservation-pass-title"
        data-cy="reservation-pass-modal"
        className="tw:fixed tw:z-50 tw:bg-white tw:shadow-lg tw:overflow-y-auto tw:border tw:border-solid tw:border-gray-light tw:bottom-0 tw:left-0 tw:right-0 tw:max-h-[90vh] tw:rounded-t-16 tw:border-b-0 animate-bottomsheet-slide-up md:animate-dialog tw:md:bottom-auto tw:md:left-1/2 tw:md:right-auto tw:md:top-1/2 tw:md:w-full tw:md:max-w-[26rem] tw:md:-translate-x-1/2 tw:md:-translate-y-1/2 tw:md:rounded-8 tw:md:border-b"
      >
        <div className="tw:flex tw:items-center tw:justify-between tw:p-base tw:border-b tw:border-solid tw:border-gray-light">
          <Text id="reservation-pass-title" size="20" weight="bold" color={Colors.colorFont} data-cy="reservation-pass-title">
            {headerTitle}
          </Text>
          <Button
            buttonType={ButtonType.text}
            onClick={onClose}
            aria-label={tCommon('close')}
            leftIcon={<MdClose size={24} className="tw:text-gray-dark" />}
            data-cy="reservation-pass-close"
          />
        </div>

        <div className="tw:relative tw:p-base tw:bg-gray-lighter">
          <div className="tw:border tw:border-solid tw:border-gray-light tw:rounded-16 tw:bg-white tw:p-base tw:shadow-card">
            <div className="tw:flex tw:items-start tw:justify-between tw:gap-base tw:mb-base">
              <div className="tw:flex tw:flex-col tw:gap-nano tw:min-w-0">
                {companyTheme?.logo && companyTheme.logo.trim().length > 0 && (
                  <img
                    src={resolveLogoSrc(companyTheme.logo)}
                    alt={companyTheme.companyName || 'Logo'}
                    className="tw:h-[29px] tw:w-auto tw:object-contain tw:mb-nano"
                    data-cy="reservation-pass-logo"
                  />
                )}
                <Text size="12" weight="regular" color={Colors.colorFont} lineHeight="2" data-cy="reservation-pass-address">
                  {garageFullAddress}
                  {garagePhone && (
                    <>
                      {garageFullAddress && ' · '}
                      <a
                        href={`tel:${reservation.garagePhone}`}
                        className="tw:text-inherit tw:no-underline"
                        data-cy="reservation-pass-phone"
                      >
                        {garagePhone}
                      </a>
                    </>
                  )}
                </Text>
              </div>

              <div className="tw:flex tw:flex-col tw:text-right tw:shrink-0 tw:gap-nano tw:items-end">
                {isTicket && ticketNumber && (
                  <Text size="24" weight="bold" color={Colors.colorFont} className="tw:tracking-[0.3px] tw:break-all" data-cy="reservation-pass-ticket">
                    {`#${ticketNumber}`}
                  </Text>
                )}
                {reservation.customerName && (
                  <Text size="20" weight="bold" color={Colors.colorFont} data-cy="reservation-pass-customer">
                    {reservation.customerName}
                  </Text>
                )}
                {vehicleText && (
                  <Text size="14" weight="regular" color={Colors.colorFont} data-cy="reservation-pass-vehicle">
                    {vehicleText}
                  </Text>
                )}
                {!isTicket && price && (
                  <Text size="14" weight="regular" color={Colors.colorGrayDarker} data-cy="reservation-pass-price">
                    {price}
                  </Text>
                )}
              </div>
            </div>

            <div className="tw:border tw:border-solid tw:border-gray-light tw:rounded-8 tw:p-base tw:mb-base">
              <ReservationTimeDetails
                startDate={startDate}
                endDate={endDate}
                garageTimezone={garageTimezone}
                variant={isTicket ? 'ticket' : 'reservation'}
              />
            </div>

            <div className="tw:relative tw:-mx-base tw:flex tw:items-center">
              <div className="tw:w-[30px] tw:h-[30px] tw:rounded-full tw:bg-gray-lighter tw:shrink-0 tw:-ml-[15px]" />

              <div className="tw:flex-1 tw:flex tw:items-center tw:gap-small tw:min-w-0">
                {!isTicket && (
                  <>
                    <div className="tw:flex-1 tw:h-px tw:bg-gray-light" />
                    {displayCode && (
                      <div className="tw:bg-gray-lighter tw:rounded-full tw:px-base tw:py-micro tw:shrink-0">
                        <Text size="16" weight="bold" color={Colors.colorFont} data-cy="reservation-pass-code">
                          {t('code', { code: displayCode })}
                        </Text>
                      </div>
                    )}
                    <div className="tw:flex-1 tw:h-px tw:bg-gray-light" />
                  </>
                )}
              </div>

              <div className="tw:w-[30px] tw:h-[30px] tw:rounded-full tw:bg-gray-lighter tw:shrink-0 tw:-mr-[15px]" />
            </div>

            <div className="tw:flex tw:flex-col tw:items-center tw:gap-base tw:mt-base">
              {qrDataUrl && (
                <img
                  src={qrDataUrl}
                  alt={t('qrCodeAlt')}
                  className={isClaimTagQr ? 'tw:w-[208px] tw:h-[208px]' : 'tw:w-[110px] tw:h-[110px]'}
                  data-cy="reservation-pass-qr"
                />
              )}
              {startDate && endDate && (
                <Text
                  size="12"
                  weight="regular"
                  color={Colors.colorGrayDarker}
                  className="tw:text-center"
                  data-cy="reservation-pass-window"
                >
                  {t('passValidWindow', {
                    start: formatReservationTimeInGarageTz(startDate, garageTimezone),
                    end: formatReservationTimeInGarageTz(endDate, garageTimezone),
                  })}
                </Text>
              )}
            </div>
          </div>
          <div className="tw:flex tw:items-center tw:justify-center tw:gap-small tw:mt-base">
            {applePassUrl && <AddToAppleWallet url={applePassUrl} />}
            {reservation.googlePassToken && <AddToGoogleWallet jwt={reservation.googlePassToken} />}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};
